import { Box3, Raycaster, Vector3 } from 'three';
import { EngineContext } from './engine-context';
import { KeyboardShortcutsManager, contentBounds, contentRoots } from './keyboard-shortcuts.manager';
import { OrbitPivotManager } from './orbit-pivot.manager';
import { loadNavlib, navlibPlatformSupported } from '../addons/space-mouse/navlib';
import type { Navlib, NavlibClient, NavlibConstructor } from '../addons/space-mouse/navlib.types';
import {
  NAVLIB_COORDINATE_SYSTEM,
  NAVLIB_FRONT_VIEW,
  applyViewExtents,
  applyViewMatrix,
  boxExtents,
  viewExtents,
  viewMatrix,
} from '../addons/space-mouse/space-mouse-view';

/** Shown in the 3Dconnexion settings panel as the profile for this app. */
export const SPACE_MOUSE_APP_NAME = 'InfinitForm';

/** Model units are millimetres. */
const UNITS_TO_METERS = 0.001;

/** Editor commands offered for the 3D mouse buttons in the 3Dconnexion settings panel. */
export const SPACE_MOUSE_COMMANDS = [
  { id: 'IF_FIT_ALL', label: 'Fit All', description: 'Fit every part in the view' },
  { id: 'IF_FOCUS_SELECTION', label: 'Focus Selection', description: 'Fit the selected parts in the view' },
  { id: 'IF_HOME_VIEW', label: 'Home View', description: 'Return to the starting view' },
  { id: 'IF_TOGGLE_GRID', label: 'Toggle Grid', description: 'Show or hide the grid' },
  { id: 'IF_TOGGLE_DIMENSIONS', label: 'Toggle Dimensions', description: 'Show or hide the model dimensions' },
] as const;

export type SpaceMouseCommandId = (typeof SPACE_MOUSE_COMMANDS)[number]['id'];

export interface SpaceMouseOptions {
  load?: () => Promise<NavlibConstructor>;
  platformSupported?: () => boolean;
  /** Runs a command that changes UI state; the driver calls in from outside Angular. */
  runCommand?: (command: () => void) => void;
  requestFrame?: (callback: (time: number) => void) => number;
  cancelFrame?: (handle: number) => void;
  now?: () => number;
  /** Called whenever `connected` changes. */
  onConnectionChange?: (connected: boolean) => void;
}

/** Refocusing the window retries a missing driver at most this often. */
const RETRY_INTERVAL_MS = 10_000;

/**
 * Drives the editor camera from a 3Dconnexion 3D mouse through the
 * 3DconnexionJS Navigation Library. The library does the navigation itself:
 * it reads the camera, model bounds and pivot through the getters below and
 * writes the new camera back through the setters. Nothing happens unless the
 * 3DxWare driver is running, and a missing driver is silent.
 */
export class SpaceMouseManager {
  private nav: Navlib | null = null;
  private created = false;
  private disposed = false;
  private moving = false;
  private frameHandle = 0;
  private pivot: Vector3 | null = null;
  private readonly pointer = new Vector3(0, 0, -1);
  private readonly ray = { origin: new Vector3(), direction: new Vector3(0, 0, -1), selectionOnly: false };
  private readonly raycaster = Object.assign(new Raycaster(), { firstHitOnly: true });
  private readonly reported = new Set<string>();
  private pointerHandler?: (event: PointerEvent) => void;
  private focusHandler?: () => void;
  private started = false;
  private connecting = false;
  private session = 0;
  private lastAttempt = -Infinity;
  private ctor: NavlibConstructor | null = null;
  private readonly load: () => Promise<NavlibConstructor>;
  private readonly now: () => number;
  private readonly onConnectionChange: (connected: boolean) => void;
  private readonly platformSupported: () => boolean;
  private readonly runCommand: (command: () => void) => void;
  private readonly requestFrame: (callback: (time: number) => void) => number;
  private readonly cancelFrame: (handle: number) => void;

  constructor(
    private readonly ctx: EngineContext,
    private readonly orbitPivot: OrbitPivotManager,
    private readonly shortcuts: KeyboardShortcutsManager,
    options: SpaceMouseOptions = {}
  ) {
    this.load = options.load ?? loadNavlib;
    this.now = options.now ?? ((): number => performance.now());
    this.platformSupported = options.platformSupported ?? ((): boolean => navlibPlatformSupported());
    this.runCommand = options.runCommand ?? ((command): void => command());
    this.requestFrame = options.requestFrame ?? ((callback): number => requestAnimationFrame(callback));
    this.cancelFrame = options.cancelFrame ?? ((handle): void => cancelAnimationFrame(handle));
    this.onConnectionChange = options.onConnectionChange ?? ((): void => undefined);
  }

  /** True once the driver has accepted this page as a 3D mouse client. */
  get connected(): boolean {
    return this.created;
  }

  /**
   * Connects to the local 3Dconnexion driver, if there is one, and tries
   * again whenever the window regains focus while not connected, so a driver
   * started or a device plugged in after the editor opened is picked up.
   */
  async start(): Promise<void> {
    if (this.started || this.disposed || !this.platformSupported()) return;
    this.started = true;

    this.pointerHandler = (event: PointerEvent): void => {
      const rect = this.ctx.canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      this.pointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
        -1
      );
    };
    this.ctx.canvas.addEventListener('pointermove', this.pointerHandler);

    this.focusHandler = (): void => {
      if (this.nav || this.now() - this.lastAttempt < RETRY_INTERVAL_MS) return;
      void this.connect();
    };
    window.addEventListener('focus', this.focusHandler);

    await this.connect();
  }

  private async connect(): Promise<void> {
    if (this.nav || this.connecting || this.disposed) return;
    this.connecting = true;
    this.lastAttempt = this.now();
    try {
      this.ctor ??= await this.load();
    } catch (e) {
      this.report('load', e);
      return;
    } finally {
      this.connecting = false;
    }
    if (this.disposed) return;

    const session = ++this.session;
    try {
      this.nav = new this.ctor(this.createClient(this.ctor, session));
      // No driver: the probe request fails and onDisconnect follows.
      if (!this.nav.connect()) this.nav = null;
    } catch (e) {
      this.report('connect', e);
      this.nav = null;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.stopFrames();
    if (this.pointerHandler) {
      this.ctx.canvas.removeEventListener('pointermove', this.pointerHandler);
      this.pointerHandler = undefined;
    }
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
      this.focusHandler = undefined;
    }
    const nav = this.nav;
    this.nav = null;
    if (!nav) return;
    try {
      // delete3dmouse() reads the view it was created on, so it is only safe after create3dmouse().
      if (this.created) nav.delete3dmouse();
      else nav.close();
    } catch (e) {
      this.report('dispose', e);
    }
    this.setCreated(false);
  }

  /** Runs one of SPACE_MOUSE_COMMANDS; unknown ids are ignored. */
  runCommandById(id: string): void {
    const commands: Record<SpaceMouseCommandId, () => void> = {
      IF_FIT_ALL: () => this.shortcuts.fitAll(),
      IF_FOCUS_SELECTION: () => this.shortcuts.focusSelection(),
      IF_HOME_VIEW: () => this.shortcuts.resetHomeView(),
      IF_TOGGLE_GRID: () => this.ctx.toggleGrid(),
      IF_TOGGLE_DIMENSIONS: () => this.ctx.toggleDimensionsHelper(),
    };
    const command = commands[id as SpaceMouseCommandId];
    if (!command) return;
    this.runCommand(command);
    this.ctx.requestRender();
  }

  private createClient(ctor: NavlibConstructor, session: number): NavlibClient {
    const ctx = this.ctx;
    const rig = (): { camera: EngineContext['camera']; controls: { target: Vector3 } } => ({
      camera: ctx.camera,
      controls: ctx.controls,
    });

    const client: NavlibClient = {
      onConnect: () => {
        this.nav?.create3dmouse(window, SPACE_MOUSE_APP_NAME);
      },
      onDisconnect: () => {
        // A session replaced by a later attempt can still report its close.
        if (session !== this.session) return;
        this.nav = null;
        this.setCreated(false);
        this.stopFrames();
      },
      on3dmouseCreated: () => {
        this.setCreated(true);
        const nav = this.nav;
        if (!nav) return;
        // The editor has its own render loop, so it supplies the frame times.
        nav.update3dcontroller({ frame: { timingSource: 1 } }).catch((e: unknown) => this.report('timing', e));
        nav.update3dcontroller({ commands: this.commandTree(ctor) }).catch((e: unknown) => this.report('commands', e));
      },
      onStartMotion: () => {
        if (this.moving) return;
        this.moving = true;
        this.frameHandle = this.requestFrame(this.pump);
      },
      onStopMotion: () => {
        this.stopFrames();
        this.orbitPivot.hideTemporary();
      },

      getCoordinateSystem: () => [...NAVLIB_COORDINATE_SYSTEM],
      getFrontView: () => [...NAVLIB_FRONT_VIEW],
      getPerspective: () => false,
      getViewRotatable: () => true,
      getConstructionPlane: () => null,
      getUnitsToMeters: () => UNITS_TO_METERS,

      getViewMatrix: () => viewMatrix(ctx.camera),
      setViewMatrix: (matrix) => {
        applyViewMatrix(rig(), matrix);
        ctx.requestRender();
      },
      getViewExtents: () => viewExtents(ctx.camera),
      setViewExtents: (extents) => {
        applyViewExtents(rig(), extents);
        ctx.requestRender();
      },

      getModelExtents: () => boxExtents(ctx.toViewBox(contentBounds(ctx))),
      getSelectionEmpty: () => ctx.selectedObjects.length === 0,
      getSelectionExtents: () => boxExtents(ctx.toViewBox(this.selectionBounds())),

      getPivotPosition: () => this.pivotPosition().toArray(),
      setPivotPosition: (position) => {
        this.pivot = new Vector3().fromArray(position);
      },
      setPivotVisible: (visible) => {
        if (visible) this.orbitPivot.showTemporary(this.pivotPosition());
        else this.orbitPivot.hideTemporary();
        ctx.requestRender();
      },

      getPointerPosition: () => {
        ctx.camera.updateMatrixWorld();
        return ctx.toViewSpace(this.pointer.clone().unproject(ctx.camera)).toArray();
      },
      setLookFrom: (origin) => {
        this.ray.origin.fromArray(origin);
      },
      setLookDirection: (direction) => {
        this.ray.direction.fromArray(direction).normalize();
      },
      setLookAperture: () => undefined,
      setSelectionOnly: (selectionOnly) => {
        this.ray.selectionOnly = selectionOnly;
      },
      getLookAt: () => this.hitTest(),

      setTransaction: (transaction) => {
        if (transaction === 0) ctx.requestRender();
      },
      setActiveCommand: (id) => {
        if (id) this.runCommandById(id);
      },
    };

    // A throw inside a callback would leave the driver waiting for an answer.
    for (const [name, fn] of Object.entries(client) as [string, (...args: unknown[]) => unknown][]) {
      (client as unknown as Record<string, unknown>)[name] = (...args: unknown[]): unknown => {
        try {
          return fn(...args);
        } catch (e) {
          this.report(name, e);
          return null;
        }
      };
    }
    return client;
  }

  /** Each frame while the puck moves: hand the driver a frame time; it writes the camera back. */
  private readonly pump = (time: number): void => {
    if (!this.moving || !this.nav) return;
    this.nav.update3dcontroller({ frame: { time } }).catch((e: unknown) => this.report('frame', e));
    this.ctx.requestRender();
    this.frameHandle = this.requestFrame(this.pump);
  };

  private setCreated(created: boolean): void {
    if (created === this.created) return;
    this.created = created;
    this.onConnectionChange(created);
  }

  private stopFrames(): void {
    this.moving = false;
    if (this.frameHandle) this.cancelFrame(this.frameHandle);
    this.frameHandle = 0;
  }

  private commandTree(ctor: NavlibConstructor): { activeSet: string; tree: unknown } {
    // 'Default' is the set id the driver shows without a set label.
    const tree = new ctor.ActionTree();
    const set = tree.push(new ctor.ActionSet('Default', SPACE_MOUSE_APP_NAME));
    const view = set.push(new ctor.Category('IF_CAT_VIEW', 'View'));
    for (const command of SPACE_MOUSE_COMMANDS) {
      view.push(new ctor.Action(command.id, command.label, command.description));
    }
    return { activeSet: 'Default', tree };
  }

  /** An Alt+Click pivot wins, then the one the driver chose, then the orbit target. */
  private pivotPosition(): Vector3 {
    const controls = this.ctx.controls as { orbitPivot?: Vector3 | null; target: Vector3 };
    return (controls.orbitPivot ?? this.pivot ?? controls.target).clone();
  }

  private selectionBounds(): Box3 {
    const box = new Box3();
    for (const object of this.ctx.selectedObjects) box.union(new Box3().setFromObject(object));
    return box;
  }

  /** Nearest visible part along the driver's look ray, for its auto-pivot and zoom. */
  private hitTest(): number[] | null {
    const roots = this.ray.selectionOnly && this.ctx.selectedObjects.length > 0
      ? this.ctx.selectedObjects
      : contentRoots(this.ctx);
    // The camera is orthographic with clipping on both sides of it, so parts
    // behind the ray origin are still in view: start the ray behind them.
    // The driver's ray is in view-frame space; parts are hit-tested in world space.
    const origin = this.ray.origin.clone().addScaledVector(this.ray.direction, -Math.abs(this.ctx.camera.far));
    const toWorld = this.ctx.viewFrame.matrixWorld;
    this.raycaster.set(origin.applyMatrix4(toWorld), this.ray.direction.clone().transformDirection(toWorld));
    // Screen-space lines (LineSegments2 edges) can only be raycast with a camera.
    this.raycaster.camera = this.ctx.camera;
    const hit = this.raycaster
      .intersectObjects(roots, true)
      .find((h) => (h.object as { isMesh?: boolean }).isMesh && isShown(h.object));
    return hit ? this.ctx.toViewSpace(hit.point).toArray() : null;
  }

  private report(where: string, error: unknown): void {
    if (this.reported.has(where)) return;
    this.reported.add(where);
    console.warn(`[SpaceMouse] ${where} failed:`, error);
  }
}

function isShown(object: { visible: boolean; parent: unknown }): boolean {
  for (let o: { visible: boolean; parent: unknown } | null = object; o; o = o.parent as typeof o) {
    if (!o.visible) return false;
  }
  return true;
}
